"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

/* ─────────────────────────────────────────────
   1. Supercharged Leads (CRM Pipeline)
 ───────────────────────────────────────────── */
export function ITSuperchargedLeads({ section }) {
  return (
    <section className="py-20 relative overflow-hidden bg-transparent w-full font-sans text-white z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">

          {/* Left: Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="w-full lg:w-[55%] relative group"
          >
            <div className="absolute inset-0 bg-[#f1833b]/5 rounded-full blur-[120px] pointer-events-none -z-10" />
            <Image
              src="/odoo/industries/it-hardware-support/pipeline.webp"
              width={750}
              height={500}
              className="w-full h-auto rounded-2xl border border-white/10 shadow-2xl hover:scale-[1.02] transition-transform duration-300"
              alt="IT CRM Pipeline"
            />
          </motion.div>

          {/* Right: Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="w-full lg:w-[40%] space-y-6"
          >
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight ${caveat.className}`}>
              Supercharged{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-white">leads</span>
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#f1833b] overflow-visible opacity-90" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8" stroke="currentColor" strokeWidth="4.5" fill="none" strokeLinecap="round" />
                </svg>
              </span>
            </h2>
            <div className="space-y-4">
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Details of every lead,</strong> including planned activities, contact information, and estimated revenue, are centralized in a drag-and-drop CRM pipeline.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   2. Customized Quotations
 ───────────────────────────────────────────── */
export function ITCustomizedQuotations({ section }) {
  const images = [
    "/odoo/industries/it-hardware-support/quotation-1.webp",
    "/odoo/industries/it-hardware-support/quotation-2.webp",
    "/odoo/industries/it-hardware-support/quotation-3.webp",
    "/odoo/industries/it-hardware-support/quotation-4.webp",
  ];

  return (
    <section className="py-20 relative overflow-hidden bg-transparent w-full font-sans text-white z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight ${caveat.className}`}>
            <span className="relative inline-block mr-3">
              <span className="relative z-10 text-white">Customized</span>
              <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#f1833b] overflow-visible opacity-90" viewBox="0 0 100 10" preserveAspectRatio="none">
                <path d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8" stroke="currentColor" strokeWidth="4.5" fill="none" strokeLinecap="round" />
              </svg>
            </span>
            quotations
          </h2>
          <p className="mt-6 text-gray-300 max-w-2xl leading-relaxed text-xl">
            <strong className="text-white font-bold">Say goodbye to boring quotations.</strong> Build a stunning quote for each sale in seconds, and reuse templates for common sales and support packages.
          </p>
        </motion.div>

        {/* Overlapping 4-image grid */}
        <div className="relative flex flex-wrap justify-center gap-6 lg:gap-0">
          <div className="flex justify-center lg:justify-end w-full lg:w-1/2 gap-6">
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="lg:mt-16 lg:-mr-16 z-10 relative">
              <Image src={images[0]} width={300} height={400} className="rounded-2xl border border-white/10 shadow-2xl w-48 md:w-56 lg:w-72 hover:scale-[1.03] transition-transform duration-300 object-cover" alt="Quotation 1" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="relative">
              <Image src={images[1]} width={300} height={400} className="rounded-2xl border border-white/10 shadow-2xl w-48 md:w-56 lg:w-72 lg:mb-16 hover:scale-[1.03] transition-transform duration-300 object-cover" alt="Quotation 2" />
            </motion.div>
          </div>
          <div className="flex justify-center lg:justify-start w-full lg:w-1/2 gap-6">
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.15 }} className="relative">
              <Image src={images[2]} width={300} height={400} className="rounded-2xl border border-white/10 shadow-2xl w-48 md:w-56 lg:w-72 lg:mb-16 lg:-ml-16 hover:scale-[1.03] transition-transform duration-300 object-cover" alt="Quotation 3" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }} className="lg:mt-16 relative">
              <Image src={images[3]} width={300} height={400} className="rounded-2xl border border-white/10 shadow-2xl w-48 md:w-56 lg:w-72 hover:scale-[1.03] transition-transform duration-300 object-cover" alt="Quotation 4" />
            </motion.div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   3. Website & eCommerce
 ───────────────────────────────────────────── */
export function ITWebsiteEcommerce({ section }) {
  return (
    <section className="py-20 relative overflow-hidden bg-transparent w-full font-sans text-white z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">

          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="w-full lg:w-[45%] space-y-6"
          >
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight ${caveat.className}`}>
              Website &{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-white">eCommerce</span>
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#f1833b] overflow-visible opacity-90" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8" stroke="currentColor" strokeWidth="4.5" fill="none" strokeLinecap="round" />
                </svg>
              </span>
            </h2>
            <div className="space-y-4">
              <p className="text-xl text-gray-300 leading-relaxed"><strong className="text-white font-bold">Build your website</strong> in seconds using Odoo's AI-based builder and page templates.</p>
              <p className="text-xl text-gray-300 leading-relaxed"><strong className="text-white font-bold">Customized selling.</strong> Your clients can pick and choose various computer components, such as RAM, CPU, or graphics card, to configure a perfect device for them.</p>
              <p className="text-xl text-gray-300 leading-relaxed"><strong className="text-white font-bold">Upsell and cross-sell</strong> additional products to boost revenue.</p>
            </div>
          </motion.div>

          {/* Right: Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="w-full lg:w-[50%] relative group"
          >
            <div className="absolute inset-0 bg-[#f1833b]/5 rounded-full blur-[120px] pointer-events-none -z-10" />
            {/* Orange sparkle doodle */}
            <div className="absolute -top-8 -right-8 w-20 h-20 pointer-events-none select-none z-20 opacity-100 group-hover:scale-110 transition-transform duration-300">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-[#f1833b]">
                <path d="M 30 70 C 45 55, 60 42, 85 34" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
                <path d="M 40 65 C 44 48, 48 30, 52 12" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
                <path d="M 48 70 C 62 66, 78 62, 94 58" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
              </svg>
            </div>
            <Image
              src="/odoo/industries/it-hardware-support/ecommerce-product.webp"
              width={680}
              height={450}
              className="w-full h-auto rounded-2xl border border-white/10 shadow-2xl hover:scale-[1.02] transition-transform duration-300"
              alt="IT eCommerce Product"
            />
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   4. Helpdesk Upgrade
 ───────────────────────────────────────────── */
export function ITHelpdeskUpgrade({ section }) {
  const points = [
    {
      title: "Omni-channel contact.",
      body: "Clients can reach you through your website, email, phone, WhatsApp, and more. Everything is centralized on your dashboard."
    },
    {
      title: "Plan and track",
      body: "onsite installations and repairs, navigate to clients' locations, and fill in custom worksheets to ensure you never miss a detail."
    },
    {
      title: "No more planning mishaps.",
      body: "Once a sale is made, an installation task is automatically created for easy follow-up."
    }
  ];

  return (
    <section className="py-20 relative overflow-hidden bg-transparent w-full font-sans text-white z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap items-center gap-4 mb-12"
        >
          <div className="h-14 w-14 relative hidden md:block">
            <Image
              src="/odoo/icons/red_mic.svg"
              fill
              className="select-none pointer-events-none object-contain"
              style={{ filter: "hue-rotate(25deg) saturate(1.4)", transform: "rotate(-10deg)" }}
              alt=""
            />
          </div>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight ${caveat.className}`}>
            Your helpdesk just got an{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-white">upgrade</span>
              <svg className="absolute inset-0 w-full h-[120%] -top-[10%] -z-10 scale-y-110 origin-center opacity-85" viewBox="0 0 100 100" preserveAspectRatio="none">
                <path d="M 5 25 C 30 18, 70 12, 95 15 C 98 25, 96 45, 95 75 C 70 78, 30 82, 5 78 C 2 60, 3 40, 5 25 Z" fill="#f1833b" />
              </svg>
            </span>
          </h2>
        </motion.div>

        {/* 3 bullet columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
          {points.map((p, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="text-gray-300 leading-relaxed text-xl"
            >
              <strong className="text-white font-bold">{p.title}</strong> {p.body}
            </motion.p>
          ))}
        </div>

        {/* Dual mockup images */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 relative">
          <div className="absolute inset-0 bg-[#f1833b]/5 rounded-full blur-[140px] pointer-events-none -z-10" />
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="w-full md:w-[60%] relative"
          >
            <Image
              src="/odoo/industries/it-hardware-support/helpdesk.webp"
              width={800}
              height={500}
              className="w-full h-auto rounded-2xl border border-white/10 shadow-2xl hover:scale-[1.02] transition-transform duration-300"
              alt="IT Helpdesk Dashboard"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="w-full md:w-[35%] relative"
          >
            {/* Rocket doodle */}
            <div className="absolute -bottom-4 -right-4 w-16 h-16 pointer-events-none select-none hidden md:block">
              <Image
                src="/odoo/icons/red_rocket.svg"
                fill
                className="object-contain"
                style={{ filter: "hue-rotate(25deg) saturate(1.4)", transform: "rotate(-10deg)" }}
                alt=""
              />
            </div>
            <Image
              src="/odoo/industries/it-hardware-support/helpdesk-2.webp"
              width={400}
              height={600}
              className="w-full h-auto rounded-2xl border border-white/10 shadow-2xl hover:scale-[1.02] transition-transform duration-300"
              alt="IT Helpdesk Mobile"
            />
          </motion.div>
        </div>

      </div>
    </section>
  );
}
